import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { backendProvider } from '../../services/backend'
import LightRays from '../effects/LightRays'
import BrainiacsLoader from '../atoms/BrainiacsLoader'
import Counter from '../atoms/Counter/Counter'
import VantaBackground from '../effects/VantaBackground'
import SettingsModal from '../modals/SettingsModal'
import SupportRequestModal from '../modals/SupportRequestModal'
import BlockedUserModal from '../modals/BlockedUserModal'
import BuyCreditsModal from '../modals/BuyCreditsModal'
import { useUserPreferences } from '../../hooks/useUserPreferences'
import { useTeamPreferences } from '../../hooks/useTeamPreferences'
import { renderCompanyLogoInWhite } from '../../utils/companyLogo'
import { AdaptiveChat } from '../chat/AdaptiveChat'
import { useChatMode } from '../../hooks/useOmnipresentAI'
import { Icon } from '@iconify/react'
import { useNotifications } from '../../hooks/useNotifications'
import { dispatchNotificationAction } from '../../services/notifications/notificationActions'
import { useActiveActions } from '../../stores/actionTracker.store'
import { useUIStore } from '../../stores/uiStore'
import { useUrlDialog } from '../../utils/urlDialogs'
import {
  normalizeBackgroundEffect,
  parseAuroraColors,
  type BackgroundEffect,
} from '../../constants/backgroundEffects'

const loadAurora = () => import('../effects/Aurora')
const Aurora = React.lazy(loadAurora)

/**
 * Renders the global BuyCreditsModal driven by uiStore.buyCreditsOpen.
 * Any caller (action hooks, service-layer catch blocks, header button) can
 * open it via `openBuyCredits()` from `stores/uiStore.ts` without prop drilling.
 */
const BuyCreditsModalWrapper: React.FC = () => {
  const isOpen = useUIStore((state) => state.buyCreditsOpen)
  const close = useUIStore((state) => state.closeBuyCredits)
  return <BuyCreditsModal isOpen={isOpen} onClose={close} />
}

const extractLeadLabel = (message: string): string | null => {
  const patterns = [
    /^Enriching\s+(.+)$/i,
    /^Starting outreach for\s+(.+)$/i,
    /^Updating enrichment for\s+(.+)$/i,
  ]

  for (const pattern of patterns) {
    const match = message.match(pattern)
    if (!match?.[1]) continue

    const rawLabel = match[1].trim()

    const multiLeadMatch = rawLabel.match(/^\d+\s+leads:\s+(.+?)(?:,\s*\.\.\.)?$/i)
    if (multiLeadMatch?.[1]) {
      return multiLeadMatch[1].trim()
    }

    if (!/^\d+\s+leads$/i.test(rawLabel)) {
      return rawLabel.replace(/,\s*\.\.\.$/, '').trim()
    }
  }

  return null
}

interface AppLayoutProps {
  children: React.ReactNode
  currentView: 'campaigns' | 'admin' | 'create'
  onGoHome?: () => void
  onBuyCredits?: () => void
  onViewInvoices?: () => void
  aiCredits?: number
  creditsLoading?: boolean
  creditsRefreshing?: boolean
  // Optional client info for user menu
  clientInfo?: { id: string; name: string; email: string; hasOutreachModule?: boolean } | null
  // Loading states
  isLoadingLead?: boolean
  /**
   * True while the route is still behind its boot overlay (loader visible,
   * content hidden). When isBootLoading transitions true→false, the header
   * slides down after a 600ms delay (matching the overlay's 500ms fade-out).
   * Routes without a boot overlay simply don't pass it — header is static.
   */
  isBootLoading?: boolean
  // Team view
  isTeamAdmin?: boolean
  teamMembersCount?: number
  onToggleTeamView?: () => void
  showTeamView?: boolean
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  currentView,
  onGoHome,
  onBuyCredits,
  onViewInvoices,
  aiCredits = 0,
  creditsLoading = false,
  creditsRefreshing = false,
  clientInfo,
  isLoadingLead = false,
  isBootLoading = false,
  isTeamAdmin,
  onToggleTeamView,
  showTeamView,
}) => {
  "use memo";
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentUser, targetEmail, setTargetEmail, signOut } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  // Settings & Support open state is URL-hash driven (#settings, #settings/team,
  // #support) so any view/modal state is deep-linkable and shareable from any
  // route. See utils/urlDialogs.ts.
  const {
    dialog: activeDialog,
    tab: urlDialogTab,
    openDialog,
    closeDialog,
    setTab: setUrlDialogTab,
  } = useUrlDialog()
  const showSettings = activeDialog === 'settings'
  const showSupportModal = activeDialog === 'support'
  const isBlockedUser = currentUser?.status === 'blocked'
  // Focused creation mode — the campaign workspace owns its chrome (title,
  // method tabs, close, actions), so the app header (logo, credits, menus)
  // stays hidden. Exception: while impersonating, the header still renders so
  // the banner's STOP IMPERSONATING button remains reachable.
  const isCreateView = currentView === 'create'
  const userMenuRef = useRef<HTMLDivElement | null>(null)
  const notificationsRef = useRef<HTMLDivElement | null>(null)
  const { data: userPreferences, isLoading: preferencesLoading } = useUserPreferences()
  // Campaign AI Assistant is gated behind the `mod_chat` team preference.
  // Until prefs are fetched we treat it as locked to avoid a flash of access.
  const { data: teamPrefs, isFetched: teamPrefsFetchedRaw } = useTeamPreferences()
  // The header waits for team prefs (partner vs default branding) before its
  // slide-in — but never indefinitely: the prefs query rides the n8n-proxy,
  // and self-hosted Appwrite's phantom-execution hangs can stall it for
  // 30s+. After a 2.5s settle window we reveal with the default Brainiacs
  // logo (from effectiveLogoUrl) so the user never sees a bare text word-mark.
  // If prefs later resolve to a partner workspace the logo swaps in place —
  // a brief img src swap is less jarring than the blank text flash.
  const [prefsSettleTimedOut, setPrefsSettleTimedOut] = useState(false)
  useEffect(() => {
    if (teamPrefsFetchedRaw) return undefined
    const t = window.setTimeout(() => setPrefsSettleTimedOut(true), 2500)
    return () => window.clearTimeout(t)
  }, [teamPrefsFetchedRaw])
  const teamPrefsFetched = teamPrefsFetchedRaw || prefsSettleTimedOut
  const canUseCampaignAssistant = teamPrefsFetched && !!teamPrefs?.modules?.chat
  // Partner teams with a custom uploaded logo (logo_url present AND not the
  // default fallback) swap the Brainiacs header logo for their own brand.
  // Persisted in sessionStorage so the logo survives a hard refresh — the
  // prefs query can take 30s+ on a cold start (phantom-execution hang), and
  // showing a blank text word-mark that long makes the app look broken.
  const [cachedLogoUrl, setCachedLogoUrl] = useState<string | undefined>(undefined)
  // Partner custom logo URL — only set when BOTH conditions hold:
  //   1. The team is a partner (teamPrefs?.partner === true)
  //   2. The team has a non-default logo (company_logo_is_default === false)
  // When either condition is false, partnerLogoUrl is undefined and the
  // default Brainiacs logo is shown instead.
  const partnerLogoUrl =
    teamPrefs?.partner && !teamPrefs?.company_logo_is_default ? teamPrefs?.logo_url : undefined
  useEffect(() => {
    if (partnerLogoUrl) {
      setCachedLogoUrl(partnerLogoUrl)
      try { sessionStorage.setItem('brainiacs-partner-logo', partnerLogoUrl) } catch { /* noop */ }
    } else {
      // Clear stale cache when partner has no custom logo — prevents broken
      // images from a previous session's cached URL.
      setCachedLogoUrl(undefined)
      try { sessionStorage.removeItem('brainiacs-partner-logo') } catch { /* noop */ }
    }
  }, [partnerLogoUrl])
  // Determine the final logo URL, using the same logic regardless of whether
  // prefs resolved fresh or the timeout fired. While partner prefs are still
  // unknown (pending), we render the default Brainiacs logo. When partner
  // branding arrives it swaps in place — no jarring text word-mark flash.
  const effectiveLogoUrl =
    partnerLogoUrl || cachedLogoUrl ||
    'https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e3f8003c17bb7fc9/files/6940e6360012a5ff6f2a/download?project=693c0579001e8b06c6d6'
  // Header slides down only when coming FROM the boot loading screen
  // (isBootLoading transitions true → false). For all other cases (navigation
  // between routes, direct visits without a loader), the header is statically
  // visible. The 600ms delay matches the LoaderOverlay's 500ms fade-out.
  const wasBooting = useRef(isBootLoading)
  useEffect(() => { wasBooting.current = isBootLoading }, [isBootLoading])
  const [bootAnimateIn, setBootAnimateIn] = useState(false)
  useEffect(() => {
    if (isBootLoading || bootAnimateIn) return
    if (!wasBooting.current) { setBootAnimateIn(true); return }
    const t = window.setTimeout(() => setBootAnimateIn(true), 600)
    return () => window.clearTimeout(t)
  }, [isBootLoading, bootAnimateIn])
  // Locked assistant → clicking opens the same activation request used in
  // Settings → Modules (contract_module mode for Campaign Assistant).
  const [showAssistantActivationModal, setShowAssistantActivationModal] = useState(false)
  // Company ID = the user's first Appwrite Team $id.
  // Loaded eagerly at boot (no enabled gate) so the user menu never waits.
  const { data: teamId } = useQuery({
    queryKey: ['team', 'id', currentUser?.uid || ''],
    queryFn: () => {
      if (!currentUser?.uid) return null;
      return backendProvider.auth.getTeamId();
    },
    staleTime: 30 * 60 * 1000, // 30 min — team ID almost never changes
    gcTime: 24 * 60 * 60 * 1000, // 24h — never evict during session
    retry: 1,
  })
  const { toggleMode } = useChatMode()
  const {
    notifications,
    unreadCount,
    isLoading: notificationsLoading,
    isTrayOpen,
    setIsTrayOpen,
    deleteNotification,
  } = useNotifications(currentUser?.uid)
  const activeActions = useActiveActions()
  const activeActionItems = activeActions.map((action) => {
    const startedAtLabel = new Date(action.startedAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })

    const statusTone =
      action.status === 'timeout'
        ? 'border-amber-400/20 bg-amber-400/10 text-amber-100'
        : 'border-cyan-300/20 bg-cyan-300/10 text-cyan-100'

    const statusLabel =
      action.status === 'timeout'
        ? 'Needs review'
        : action.status === 'pending'
          ? 'Queued'
          : 'In progress'

    const actionLabel =
      action.type === 'enrich'
        ? 'Enrichment'
        : action.type === 'outreach'
          ? 'Outreach'
          : 'Export'

    const primaryLeadName = action.leadNames?.[0] || null

    return {
      id: action.id,
      title: primaryLeadName || `${actionLabel} in ${action.campaignName}`,
      subtitle: primaryLeadName
        ? `${actionLabel} in ${action.campaignName}`
        : action.leadCount === 1
          ? '1 lead selected'
          : `${action.leadCount.toLocaleString()} leads selected`,
      meta:
        action.leadCount === 1
          ? `1 lead selected`
          : `${action.leadCount.toLocaleString()} leads selected`,
      startedAtLabel,
      statusLabel,
      statusTone,
    }
  })
  const creditModules = [
    {
      key: 'leads-intelligence',
      title: 'Leads Intelligence',
      items: [
        {
          key: 'generate',
          label: 'Generate',
          description: 'Lead Generator',
          price: '0.45 €',
          unit: 'x lead',
        },
        {
          key: 'enrich',
          label: 'Enrich & ICP',
          description: 'Enrichment',
          price: '5 €',
          unit: 'x lead',
        },
      ],
    },
    {
      key: 'sales-intelligence',
      title: 'Sales Intelligence',
      items: [
        {
          key: 'outreach-icp',
          label: 'Messaging & Outreach',
          description: 'Multichannel Text',
          addon: {
            label: 'Extra Voice addon',
            price: '+10 €',
            unit: 'x lead',
          },
          price: '15 €',
          unit: 'x lead',
        },
        {
          key: 'followup-convert',
          label: 'Follow up & Convert',
          description: 'AI Follow-up, qualification, and closing',
          price: '50 €',
          unit: 'x SQL',
        },
      ],
    },
  ]

  // Background Effect Logic with transition handling.
  // Minimalist ('none') is the default; removed effects (vanta-dots/vanta-fog)
  // fall back to it so legacy stored values never render a dead option.
  const rawEffect = userPreferences?.background_effect
  const currentEffect = normalizeBackgroundEffect(rawEffect)
  const backgroundOpacity = userPreferences?.background_opacity ?? 0.7
  const previousEffectRef = useRef<BackgroundEffect | null>(null) // null = not initialized yet
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [activeEffect, setActiveEffect] = useState<BackgroundEffect>(currentEffect)

  // Callback when new background effect is ready
  const handleBackgroundReady = useCallback(() => {
    setIsTransitioning(false)
  }, [])

  // Handle background transitions - hide old effect while new one loads
  useEffect(() => {
    // First load: set immediately without transition
    if (previousEffectRef.current === null) {
      previousEffectRef.current = currentEffect
      setActiveEffect(currentEffect)
      return
    }

    // Subsequent changes: animate transition
    if (previousEffectRef.current !== currentEffect) {
      // Start transition: fade out current background
      setIsTransitioning(true)

      // After fade out, switch to new effect
      const switchTimer = setTimeout(() => {
        setActiveEffect(currentEffect)
        previousEffectRef.current = currentEffect

        // Synchronous effects are ready immediately. WebGL-backed effects clear
        // the transition through handleBackgroundReady after their first frame.
        if (currentEffect === 'lightrays' || currentEffect === 'none') {
          setTimeout(() => {
            setIsTransitioning(false)
          }, 100)
        }
        // For Vanta effects, wait for onReady callback
      }, 300) // Match CSS transition duration

      return () => {
        clearTimeout(switchTimer)
      }
    }
  }, [currentEffect])

  // Check if current user is super admin
  const { isAdmin } = useAuth()

  const handleGoHome = useCallback(() => {
    onGoHome?.()
    navigate({ to: '/campaigns' })
  }, [navigate, onGoHome])

  // Handle sign out
  const handleSignOut = useCallback(async () => {
    try {
      setShowUserMenu(false)
      await signOut()
      window.location.reload()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }, [signOut])

  // Handle stop impersonation
  const handleStopImpersonation = useCallback(() => {
    console.log('🎭 Stopping impersonation, returning to admin dashboard')

    // Clear impersonation state
    setTargetEmail(null)

    // Clear all cached queries to avoid showing stale data
    queryClient.clear()

    // Navigate back to admin dashboard
    navigate({ to: '/admin' })
  }, [setTargetEmail, queryClient, navigate])

  useEffect(() => {
    if (!showUserMenu) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [showUserMenu])

  useEffect(() => {
    setShowNotifications(isTrayOpen)
  }, [isTrayOpen])

  useEffect(() => {
    if (!showNotifications) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!notificationsRef.current?.contains(event.target as Node)) {
        setShowNotifications(false)
        setIsTrayOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [setIsTrayOpen, showNotifications])

  return (
    <div className="app-shell min-h-screen bg-[#050505] text-white font-sans selection:bg-[#5260FF]/90 selection:text-white relative">
      {/* Background Effects with transition - hidden while preferences load */}
      <div
        className="fixed inset-0 z-0 pointer-events-none transition-opacity duration-300"
        style={{ opacity: preferencesLoading || isTransitioning ? 0 : 1 }}
      >
        {activeEffect === 'lightrays' && <LightRays opacity={backgroundOpacity} />}
        {activeEffect === 'aurora' && (
          <React.Suspense fallback={null}>
            <div className="h-full w-full" style={{ opacity: backgroundOpacity }}>
              <Aurora
                colorStops={parseAuroraColors(userPreferences?.background_aurora_colors)}
                blend={0.95}
                amplitude={1}
                speed={0.4}
                onReady={handleBackgroundReady}
              />
            </div>
          </React.Suspense>
        )}
        {activeEffect === 'vanta-waves' && (
          <VantaBackground
            effect="waves"
            opacity={backgroundOpacity}
            onReady={handleBackgroundReady}
          />
        )}
      </div>

      {/* Loading Lead Overlay */}
      {isLoadingLead && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl px-10 py-8 shadow-2xl">
            <BrainiacsLoader variant="section" size="md" message="Loading lead details..." className="py-2" />
          </div>
        </div>
      )}

      {/* Fixed Header — always rendered and visible. When the route had a boot
          loading screen (isBootLoading was true at mount and has since turned
          false) the header slides down from the top after a 600ms settle —
          matching the LoaderOverlay's 500ms fade-out. For all other cases
          (navigation between routes, direct visits without a boot loader) the
          header is statically visible with no slide-in animation. */}
      <header
        className={`app-shell__header fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/5 glass-header border-b border-white/10 shadow-2xl ${
          bootAnimateIn
            ? 'translate-y-0 opacity-100 transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
            : '-translate-y-full opacity-0'
        }${isCreateView ? ' app-shell__header--hidden' : ''}`}
      >
        {/* Impersonation Banner */}
        {targetEmail && (
          <div className="app-shell__banner bg-[#5260FF]/90 text-white px-6 py-2.5 flex items-center justify-between font-bold text-[10px] uppercase tracking-widest animate-in slide-in-from-top duration-300">
            <div className="flex items-center gap-3">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="flex items-center gap-2">
                SYSTEM IMPERSONATION ACTIVE <span className="opacity-40">/</span> {targetEmail}
              </span>
            </div>
            <button
              onClick={handleStopImpersonation}
              className="bg-white/15 text-white px-4 py-1 rounded-md hover:bg-white/25 transition-all cursor-pointer text-[9px] font-black tracking-tight"
            >
              STOP IMPERSONATING
            </button>
          </div>
        )}
        <div className="app-shell__inner max-w-[1600px] mx-auto px-6 h-20 flex items-center justify-between gap-4">
          {/* Left Section - Logo */}
          {/* Partner teams (team pref `partner` = true) that uploaded a custom
              logo get it here instead of the Brainiacs default. The fallback
              branch is byte-identical to the historical hardcoded logo. */}
          <div className="app-shell__brand flex items-center flex-shrink-0 gap-2.5">
            <button
              type="button"
              onClick={handleGoHome}
              className="flex items-center hover:opacity-80 transition-opacity cursor-pointer"
              aria-label="Go to home"
            >
              {partnerLogoUrl || cachedLogoUrl ? (
                <>
                  <img
                    src={partnerLogoUrl || cachedLogoUrl}
                    alt={teamPrefs?.team_name || 'Team'}
                    className={`app-shell__partner-logo object-contain ${renderCompanyLogoInWhite() ? 'brightness-0 invert' : ''}`}
                    onError={(e) => {
                      // Partner logo failed to load (CORS, missing file, broken URL).
                      // Hide the broken image and show the team name as text instead.
                      e.currentTarget.style.display = 'none'
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement | null
                      if (fallback) fallback.style.display = 'block'
                    }}
                  />
                  <span
                    className="hidden font-black tracking-tight text-2xl text-white"
                    data-partner-logo-fallback
                  >
                    {teamPrefs?.team_name || 'Team'}
                  </span>
                </>
              ) : (
                <img
                  src={effectiveLogoUrl}
                  alt="Brainiacs AI"
                  className="app-shell__logo h-24 object-contain pt-5"
                />
              )}
            </button>
            {/* Co-branding credit — keeps Brainiacs visible when a partner's
                own logo replaces ours in the header.
                Only renders when the partner logo is actively shown (not the
                default Brainiacs fallback), so non-partner users and partners
                without a custom logo never see "Powered by Brainiacs" text. */}
            {partnerLogoUrl || cachedLogoUrl ? (
              <span className="hidden md:flex items-center gap-1.5 border-l border-white/15 pl-2.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white/40 whitespace-nowrap">
                Powered by
                <span className="font-black tracking-[0.08em] text-white/70">Brainiacs AI</span>
              </span>
            ) : null}
          </div>

          {/* Center Section */}
          <div className="flex-1 flex items-center justify-center">
            {/* Campaign Creator View - No header buttons needed, modal has X close */}
          </div>

          {/* Right Section - Credits, Admin Button, AI Assistant, User Menu */}
          <div className="app-shell__actions flex items-center gap-3 sm:gap-4 md:gap-6 flex-shrink-0">
            {/* AI Credits Counter with Add Button */}
            <div className="app-shell__credits ml-2 flex items-center gap-3 pl-6 pr-1 py-1 md:ml-3 md:pl-8">
              <div className="group/credits relative flex items-center gap-3">
                <div className="flex items-center gap-2.5">
                  {/* Stacked icon-with-spinner: the BrainiacsLoader sits as the
                      background layer and the info-circle icon renders centered
                      on top, so the spinner ring wraps around the icon edge. */}
                  <span className="relative inline-flex h-[22px] w-[22px] items-center justify-center">
                    {(creditsLoading || creditsRefreshing) && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <BrainiacsLoader size="sm" />
                      </span>
                    )}
                    <Icon icon="bx:info-circle" className={`relative h-5 w-5 transition-colors group-hover/credits:text-cyan-300/70 ${creditsLoading || creditsRefreshing ? 'text-cyan-400' : 'text-white/25'}`} />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
                      <span>Credits</span>
                    </div>
                    {/* Odometer Counter: holds the previous value and rolls each
                        digit to the next one, so background credit changes are
                        visible instead of a silent text swap. Falls back to the
                        loading dots while the balance is still unknown.
                        Credits render as whole numbers — the balance carries
                        decimals (1,234.56) but the header shows the integer. */}
                    <span className="block min-w-[5ch] text-base font-black leading-none text-white tabular-nums">
                      {aiCredits ? (
                        <>
                          <Counter value={Math.round(Number(aiCredits))} fontSize={16} gap={0} fontWeight={900} gradientHeight={0} />
                          <span className="sr-only">{Math.round(Number(aiCredits))} credits</span>
                        </>
                      ) : '...'}
                    </span>
                  </div>
                </div>

                <div className="pointer-events-none absolute left-0 top-full z-40 hidden w-[340px] pt-4 group-hover/credits:block">
                  <div className="overflow-hidden rounded-3xl border border-cyan-300/15 bg-[linear-gradient(160deg,rgba(16,18,22,0.98),rgba(7,7,9,0.98))] shadow-[0_28px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl">
                    <div className="px-5 py-4">
                      <div className="text-[11px] font-black uppercase tracking-[0.24em] text-cyan-200/70">Credits Pricing</div>
                      <p className="mt-2 text-sm leading-6 text-gray-400">
                        Pricing by action for each lead.
                      </p>
                    </div>
                    <div className="space-y-3 px-3 pb-3">
                      {creditModules.map((module) => (
                        <div
                          key={module.key}
                          className="rounded-2xl border border-cyan-300/10 bg-cyan-300/[0.05] px-5 py-4"
                        >
                          <div className="flex items-center gap-3 px-1">
                            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-white/55">
                              {module.title}
                            </div>
                            <div className="h-px flex-1 bg-white/8" />
                          </div>
                          <div className="mt-3 space-y-3 px-1">
                            {module.items.map((item) => (
                              <React.Fragment key={item.key}>
                                <div className="flex items-start justify-between gap-4">
                                  <div className="min-w-0">
                                    <div className="text-sm leading-5 text-gray-200">{item.label}</div>
                                    {item.description && (
                                      <p className="mt-1 max-w-[190px] text-xs leading-5 text-gray-500">
                                        {item.description}
                                      </p>
                                    )}
                                  </div>
                                  <span className="shrink-0 pt-0.5 text-sm text-white/90">
                                    <span className="font-bold">{item.price}</span>
                                    <span className="ml-1 font-normal text-white/55">{item.unit}</span>
                                  </span>
                                </div>
                                {item.addon && (
                                  <div className="flex items-start justify-between gap-4">
                                    <span className="text-xs leading-5 text-cyan-100/75">{item.addon.label}</span>
                                    <span className="shrink-0 pt-0.5 text-sm text-white/90">
                                      <span className="font-bold">{item.addon.price}</span>
                                      <span className="ml-1 font-normal text-white/55">{item.addon.unit}</span>
                                    </span>
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-6 w-px bg-white/5"></div>

              {/* Buy Credits Button */}
              <button
                onClick={onBuyCredits}
                className="group relative mr-3 overflow-hidden rounded-xl bg-white/5 px-4 py-2 text-white transition-all duration-200 hover:bg-white/10 cursor-pointer"
                title="Buy Credits"
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(82,96,255,0.16),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(21,251,255,0.08),transparent_35%)] opacity-60 transition-opacity duration-300 group-hover:opacity-80" />
                <div className="relative flex items-center gap-2.5">
                  <span className="app-shell__action-icon flex h-8 w-8 items-center justify-center">
                    <svg
                      className="h-4 w-4 text-cyan-50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.2 6h12.4M10 21a1 1 0 100-2 1 1 0 000 2zm8 0a1 1 0 100-2 1 1 0 000 2z" />
                    </svg>
                  </span>
                  <span className="min-w-0 text-left">
                    <span className="block text-xs font-semibold text-white">Buy Credits</span>
                  </span>
                </div>
              </button>
            </div>

            {/* Admin Dashboard Button - Only show for super admin */}
            {isAdmin && (
              currentView === 'admin' ? (
                <Link
                  to="/campaigns"
                  className="group relative overflow-hidden rounded-xl bg-white/5 px-3 py-2 sm:px-4 sm:py-2.5 text-white transition-all duration-200 hover:bg-white/10 cursor-pointer"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(52,211,153,0.15),transparent_45%)] opacity-60 transition-opacity duration-300 group-hover:opacity-80" />
                  <div className="relative flex items-center gap-2 sm:gap-3">
                    <span className="app-shell__action-icon flex h-9 w-9 items-center justify-center">
                      <svg className="w-4 h-4 text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                    </span>
                    <span className="min-w-0 text-left">
                      <span className="block text-[10px] font-black uppercase tracking-[0.22em] text-white/92 sm:text-[11px]">Go Back</span>
                      <span className="hidden text-[10px] font-medium tracking-[0.18em] text-emerald-200/75 sm:block">Back to campaigns</span>
                    </span>
                  </div>
                </Link>
              ) : (
                <Link
                  to="/admin"
                  className="group relative overflow-hidden rounded-xl bg-white/5 px-3 py-2 sm:px-4 sm:py-2.5 text-white transition-all duration-200 hover:bg-white/10 cursor-pointer"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(52,211,153,0.15),transparent_45%)] opacity-60 transition-opacity duration-300 group-hover:opacity-80" />
                  <div className="relative flex items-center gap-2 sm:gap-3">
                    <span className="app-shell__action-icon flex h-9 w-9 items-center justify-center">
                      <svg className="w-4 h-4 text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </span>
                    <span className="min-w-0 text-left">
                      <span className="block text-[10px] font-black uppercase tracking-[0.22em] text-white/92 sm:text-[11px]">Admin</span>
                      <span className="hidden text-[10px] font-medium tracking-[0.18em] text-emerald-200/75 sm:block">Control center</span>
                    </span>
                  </div>
                </Link>
              )
            )}

            {/* Team View Button - Always show for Appwrite teamadmin users on campaigns pages */}
            {isTeamAdmin && currentView === 'campaigns' && (
              <button
                onClick={onToggleTeamView}
                className="group relative overflow-hidden rounded-xl bg-white/5 px-3 py-2 sm:px-4 sm:py-2.5 text-white transition-all duration-200 hover:bg-white/10 cursor-pointer"
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(192,132,252,0.15),transparent_45%)] opacity-60 transition-opacity duration-300 group-hover:opacity-80" />
                <div className="relative flex items-center gap-2 sm:gap-3">
                  <span className="app-shell__action-icon flex h-9 w-9 items-center justify-center">
                    <svg className="w-4 h-4 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                  <span className="min-w-0 text-left">
                    <span className="block text-[10px] font-black uppercase tracking-[0.22em] text-white/92 sm:text-[11px]">{showTeamView ? 'MY CAMPAIGNS' : 'TEAM'}</span>
                    <span className="hidden text-[10px] font-medium tracking-[0.18em] text-purple-200/75 sm:block">{showTeamView ? 'Back to campaigns' : 'View'}</span>
                  </span>
                </div>
              </button>
            )}

            {/* Omnipresent AI Trigger — gated by the `mod_chat` team preference */}
            {canUseCampaignAssistant ? (
              <button
                onClick={() => toggleMode()}
                className="app-shell__action-card group relative ml-2 overflow-hidden rounded-2xl border border-[#5260FF]/25 bg-[linear-gradient(135deg,rgba(82,96,255,0.16),rgba(8,12,22,0.94))] px-3 py-2 text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-400/40 hover:shadow-[0_14px_34px_rgba(82,96,255,0.24)] cursor-pointer sm:ml-3 sm:px-4 sm:py-2.5 md:ml-5"
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.28),transparent_42%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="relative flex items-center gap-2 sm:gap-3">
                  <Icon icon="solar:chat-round-dots-bold" className="h-4 w-4 shrink-0 text-cyan-100" aria-hidden="true" />
                  <span className="min-w-0 text-left self-center">
                    <span className="block whitespace-nowrap text-[9px] font-black uppercase tracking-[0.2em] text-cyan-100/72 sm:text-[10px]">Chat Campaign</span>
                    <span className="block whitespace-nowrap text-[10px] font-black uppercase tracking-[0.22em] text-white/92 sm:text-[11px]">AI Assistant</span>
                  </span>
                  <span className="text-[9px] font-black uppercase tracking-[0.24em] text-cyan-200">
                    Beta
                  </span>
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowAssistantActivationModal(true)}
                title="Campaign Assistant is not active for your team. Click to request activation."
                aria-label="Campaign AI Assistant is not active. Click to request activation."
                className="group relative ml-2 flex cursor-pointer items-center gap-2 overflow-hidden rounded-2xl border border-white/8 bg-black/30 px-3 py-2 text-gray-500 transition-colors hover:border-cyan-300/25 hover:text-gray-300 sm:ml-3 sm:gap-3 sm:px-4 sm:py-2.5 md:ml-5"
              >
                <Icon icon="solar:lock-keyhole-bold" className="h-4 w-4 shrink-0 text-gray-500 transition-colors group-hover:text-cyan-200" aria-hidden="true" />
                <span className="min-w-0 text-left self-center">
                  <span className="block whitespace-nowrap text-[9px] font-black uppercase tracking-[0.2em] text-gray-500 sm:text-[10px]">Chat Campaign</span>
                  <span className="block whitespace-nowrap text-[10px] font-black uppercase tracking-[0.22em] text-gray-500 sm:text-[11px]">AI Assistant</span>
                </span>
                <span className="text-[9px] font-black uppercase tracking-[0.24em] text-gray-600">
                  Beta
                </span>
              </button>
            )}

            {/* Notifications */}
            <div ref={notificationsRef} className="relative">
              <button
                onClick={() => {
                  const nextOpen = !showNotifications
                  setShowNotifications(nextOpen)
                  setIsTrayOpen(nextOpen)
                }}
                className="relative flex h-11 w-11 items-center justify-center text-white transition-colors duration-200 hover:text-gray-300"
                title="Notifications"
              >
                <Icon icon="solar:bell-linear" className="h-5 w-5" aria-hidden="true" />
                {activeActions.length > 0 && (
                  <span className="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-black text-white shadow-lg shadow-blue-500/30 animate-pulse">
                    {activeActions.length}
                  </span>
                )}
                {unreadCount > 0 && activeActions.length === 0 && (
                  <span className="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-cyan-400 px-1 text-[10px] font-black text-black">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-3 w-[360px] overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-2xl shadow-black/50 z-50">
                  <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-[0.2em] text-white/60">Notifications</div>
                      <div className="mt-1 text-xs text-gray-500">Live activity and recent history</div>
                    </div>
                    {notificationsLoading && (
                      <svg className="h-4 w-4 animate-spin text-cyan-300" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                    )}
                  </div>

                  <div className="max-h-[380px] overflow-y-auto p-2">
                    {activeActionItems.length > 0 && (
                      <div className="mb-3">
                        <div className="px-3 pb-2 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-300/80">
                          In Progress
                        </div>
                        {activeActionItems.map((item) => (
                          <div
                            key={item.id}
                            className="mb-2 rounded-xl border border-cyan-300/10 bg-cyan-300/[0.06] px-3 py-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold leading-5 text-white/92">{item.title}</p>
                                <p className="mt-1 text-xs text-gray-300">{item.subtitle}</p>
                                <p className="mt-1 text-[11px] text-gray-500">{item.meta}</p>
                              </div>
                              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                                {item.startedAtLabel}
                              </span>
                            </div>
                            <div className={`mt-3 inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] ${item.statusTone}`}>
                              {item.statusLabel}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {notifications.length > 0 && (
                      <div className="px-3 pb-2 text-[10px] font-black uppercase tracking-[0.18em] text-white/45">
                        Recent Notifications
                      </div>
                    )}

                    {activeActionItems.length === 0 && notifications.length === 0 ? (
                      <div className="px-3 py-8 text-center text-sm text-gray-500">
                        No recent notifications.
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className="mb-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-3 transition-colors hover:border-white/10 hover:bg-white/[0.05]"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              {extractLeadLabel(notification.message) && (
                                <p className="truncate text-sm font-semibold leading-5 text-white">
                                  {extractLeadLabel(notification.message)}
                                </p>
                              )}
                              <p className="mt-1 text-sm leading-5 text-white/82">{notification.message}</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                                {notification.createdAt
                                  ? new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                  : ''}
                              </span>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  void deleteNotification(notification.id)
                                }}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/8 bg-white/[0.03] text-white/35 transition-all hover:border-white/14 hover:bg-white/[0.06] hover:text-white/80"
                                aria-label="Delete notification"
                                title="Delete notification"
                              >
                                <Icon icon="solar:trash-bin-trash-linear" className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                          {notification.action && (
                            <button
                              type="button"
                              onClick={() => dispatchNotificationAction(notification.action)}
                              className="mt-3 text-[11px] font-black uppercase tracking-[0.16em] text-cyan-300 transition-colors hover:text-cyan-200"
                            >
                              Open
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="app-shell__avatar w-11 h-11 rounded-full backdrop-blur-md bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all duration-200 cursor-pointer overflow-hidden"
                title={currentUser?.email || 'User'}
              >
                {currentUser?.photoURL ? (
                  <img
                    src={currentUser.photoURL}
                    alt={currentUser.displayName || currentUser.email || 'User'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                      const svg = target.nextElementSibling as HTMLElement
                      if (svg) svg.style.display = 'block'
                    }}
                  />
                ) : null}
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ display: currentUser?.photoURL ? 'none' : 'block' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                    onClick={() => setShowUserMenu(false)}
                  ></div>
                  <div className="absolute right-0 mt-3 w-80 bg-[#0a0a0a] backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 z-50 overflow-hidden">
                    {/* User Info Section */}
                    <div className="p-4 border-b border-white/5">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {currentUser?.photoURL ? (
                            <img
                              src={currentUser.photoURL}
                              alt={currentUser.displayName || currentUser.email || 'User'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Signed in as</p>
                          {currentUser?.displayName && (
                            <p className="text-sm font-semibold text-white truncate">
                              {currentUser.displayName}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 truncate">{currentUser?.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Account IDs — Appwrite identifiers for support/debugging.
                        Renders regardless of clientInfo (that query can fail or
                        fall back to prefs); every field here comes from the
                        signed-in user, the team prefs, or the team-id query. */}
                    <div className="px-4 py-3 border-b border-white/5 bg-[#5260FF]/5">
                      <p className="text-[11px] text-gray-500 mb-2 uppercase tracking-wider">Client Information</p>
                      <div className="space-y-1.5">
                        <div>
                          <p className="text-[11px] text-gray-500">User ID</p>
                          <p className="text-xs font-mono text-white truncate" title={currentUser?.uid}>
                            {currentUser?.uid || '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">Company ID</p>
                          <p className="text-xs font-mono text-white truncate" title={teamId || undefined}>
                            {teamId || '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">Company Name</p>
                          <p className="text-xs font-medium text-white truncate" title={teamPrefs?.team_name || undefined}>
                            {teamPrefs?.team_name || '—'}
                          </p>
                        </div>
                      </div>

                      {/* View Invoices — disabled until the billing surface ships. */}
                      <button
                        disabled
                        title="Invoices are not available yet"
                        className="w-full mt-3 px-4 py-2.5 bg-white/5 border border-white/10 text-white/40 rounded-xl font-medium text-sm flex items-center justify-center gap-2 cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        View Invoices
                      </button>
                    </div>

                    {/* Actions Section */}
                    <div className="p-3 space-y-2">
                      <button
                        onClick={() => {
                          openDialog('support')
                          setShowUserMenu(false)
                        }}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200 flex items-center gap-3 border border-transparent hover:border-white/10"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4v-4z" />
                        </svg>
                        Report Bug or Contact
                      </button>

                      <button
                        onClick={() => {
                          openDialog('settings')
                          setShowUserMenu(false)
                        }}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200 flex items-center gap-3 border border-transparent hover:border-white/10"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings
                      </button>

                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-all duration-200 flex items-center gap-3 border border-red-500/20 hover:border-red-500/30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className={`relative z-10${targetEmail ? ' pt-10' : ''}`}>{children}</div>

      {/* Omnipresent AI Chat — only mounted when the Campaign Assistant module is active */}
      {canUseCampaignAssistant && <AdaptiveChat />}

      {/* Settings Modal — tab deep-linkable via #settings/<tab> */}
      <SettingsModal
        isOpen={showSettings}
        initialTab={activeDialog === 'settings' && urlDialogTab ? (urlDialogTab as any) : undefined}
        onClose={() => closeDialog()}
      />

      <BlockedUserModal
        isOpen={isBlockedUser}
        onContactSupport={() => openDialog('support')}
      />

      <SupportRequestModal
        isOpen={showSupportModal}
        onClose={() => closeDialog()}
        userEmail={currentUser?.email}
        userName={currentUser?.displayName}
        impersonatedEmail={targetEmail}
        clientInfo={clientInfo}
      />

      {/* Campaign Assistant activation request — the SAME modal Settings →
          Modules uses (contract_module mode), triggered by the locked
          AI Assistant button when mod_chat is off. */}
      <SupportRequestModal
        isOpen={showAssistantActivationModal}
        onClose={() => setShowAssistantActivationModal(false)}
        userEmail={currentUser?.email}
        userName={currentUser?.displayName}
        impersonatedEmail={targetEmail}
        clientInfo={clientInfo}
        mode="contract_module"
        requestedModules={['Campaign Assistant']}
      />

      {/* Buy Credits Modal — global trigger from anywhere via uiStore.openBuyCredits() */}
      <BuyCreditsModalWrapper />
    </div>
  )
}
